import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from "react";
import { useRemSize } from "../../../../hooks/useRemSize";
import { thousandize } from "../../../../util/formatting";
interface BarChartProps {
  value: number;
  minimumThreshold: number;
  maximumThreshold: number;
  taxBenefit: number;
  labels?: {
    maximumThresholdLabel?: string;
    minimumThresholdLabel?: string;
    currentValueLabel?: string;
    taxBenefitLabel?: string;
  };
  locale?: string;
}
interface Dimensions {
  width: number;
  height: number;
}
interface Padding {
  left: number;
  right: number;
  top: number;
  bottom: number;
}
export const TaxDeductionBarChart: React.FC<BarChartProps> = t0 => {
  const $ = _c(29);
  const {
    value,
    minimumThreshold,
    maximumThreshold,
    taxBenefit,
    labels,
    locale
  } = t0;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: 0,
      height: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dimensions, setDimensions] = useState(t1);
  const remSize = useRemSize();
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (!containerRef.current) {
        return;
      }
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const {
            width,
            height
          } = entry.contentRect;
          setDimensions({
            width,
            height
          });
        }
      });
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const t4 = 3 * remSize;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      left: 0,
      right: 0,
      top: t4,
      bottom: 0
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const padding = t5;
  const chartWidth = Math.max(dimensions.width - padding.left - padding.right, 0);
  const chartHeight = Math.max(dimensions.height - padding.top - padding.bottom, 0);
  let t6;
  if ($[5] !== chartHeight || $[6] !== maximumThreshold) {
    t6 = val => val / maximumThreshold * chartHeight;
    $[5] = chartHeight;
    $[6] = maximumThreshold;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const scale = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      width: "100%",
      height: "100%",
      position: "relative"
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== chartWidth || $[10] !== dimensions.height || $[11] !== dimensions.width || $[12] !== labels?.currentValueLabel || $[13] !== labels?.maximumThresholdLabel || $[14] !== labels?.minimumThresholdLabel || $[15] !== labels?.taxBenefitLabel || $[16] !== locale || $[17] !== maximumThreshold || $[18] !== minimumThreshold || $[19] !== padding.bottom || $[20] !== padding.left || $[21] !== padding.top || $[22] !== remSize || $[23] !== scale || $[24] !== taxBenefit || $[25] !== value) {
    t8 = dimensions.width !== 0 && dimensions.height !== 0 && <svg width={dimensions.width} height={dimensions.height} style={{
      position: "absolute",
      top: 0,
      left: 0,
      overflow: "visible"
    }}><defs><pattern id="diagonalHatch" patternUnits="userSpaceOnUse" width="4" height="4"><path d={"M-1,1 l2,-2\n                     M0,4 l4,-4\n                     M3,5 l2,-2"} style={{
            stroke: "#fafafa",
            strokeWidth: 1
          }} /></pattern></defs><line x1={0} y1={padding.top} x2={0} y2={dimensions.height - padding.bottom} stroke="black" strokeWidth="1" /><line x1={-remSize * 0.5} y1={padding.top + 0.5} x2={0} y2={padding.top + 0.5} stroke="black" strokeWidth="1" /><text x={-remSize * 1} y={padding.top - remSize * 0.6} textAnchor="end" alignmentBaseline="middle">{labels?.maximumThresholdLabel || "Maks skattefradrag"}</text><text x={-remSize * 1} y={padding.top + remSize * 0.6} textAnchor="end" alignmentBaseline="middle" fontSize="0.75rem">{thousandize(maximumThreshold, locale)} kr</text><g style={{
        transition: "all 0.5s",
        opacity: value >= minimumThreshold ? 0 : 1
      }}><line x1={-5} y1={dimensions.height - padding.bottom - scale(minimumThreshold)} x2={0} y2={dimensions.height - padding.bottom - scale(minimumThreshold)} stroke="black" strokeWidth="1" /><text x={-remSize * 1} y={dimensions.height - padding.bottom - scale(minimumThreshold) - remSize * 0.6} textAnchor="end" alignmentBaseline="middle">{labels?.minimumThresholdLabel || "Minste grense"}</text><text x={-remSize * 1} y={dimensions.height - padding.bottom - scale(minimumThreshold) + remSize * 0.6} textAnchor="end" alignmentBaseline="middle" fontSize="0.75rem">{thousandize(minimumThreshold, locale)} kr</text></g>{(() => {
        const LABEL_HEIGHT = 2 * remSize;
        const deductionY = dimensions.height - padding.bottom - scale(Math.min(value, maximumThreshold)) + 0.5;
        const benefitY = dimensions.height - padding.bottom - scale(taxBenefit);
        const distance = Math.abs(deductionY - benefitY);
        const labelOverlap = distance - LABEL_HEIGHT - remSize * 0.5;
        const isOverlapping = labelOverlap < 0;
        const overlapAdjustment = isOverlapping ? Math.abs(labelOverlap) / 2 : 0;
        return <><g style={{
            transition: "all 0.5s",
            transform: `translateY(${deductionY}px)`,
            opacity: value >= minimumThreshold && value < maximumThreshold ? 1 : 0
          }}><line x1={-remSize * 0.5} y1={0} x2={0} y2={0} stroke="black" strokeWidth="1" /><g style={{
              transition: "all 0.5s",
              transform: `translateY(${-overlapAdjustment}px)`
            }}><text x={-remSize * 1} y={-remSize * 0.6} textAnchor="end" alignmentBaseline="middle" paintOrder="stroke" fill="black" stroke="#fafafa" strokeWidth="6">{labels?.currentValueLabel || "Skattefradrag"}</text><text x={-remSize * 1} y={remSize * 0.6} textAnchor="end" alignmentBaseline="middle" fontSize="0.75rem" paintOrder="stroke" fill="black" stroke="#fafafa" strokeWidth="6">{thousandize(Math.min(value, maximumThreshold), locale)} kr</text></g></g><g style={{
            transition: "all 0.5s",
            transform: `translateY(${benefitY}px)`,
            opacity: taxBenefit > 0 ? 1 : 0
          }}><line x1={-remSize * 0.5} y1={0} x2={0} y2={0} stroke="black" strokeWidth="1" /><g style={{
              transition: "all 0.5s",
              transform: `translateY(${overlapAdjustment}px)`
            }}><text x={-remSize * 1} y={-remSize * 0.6} textAnchor="end" alignmentBaseline="middle">{labels?.taxBenefitLabel || "Du f\xE5r tilbake"}</text><text x={-remSize * 1} y={remSize * 0.6} textAnchor="end" alignmentBaseline="middle" fontSize="0.75rem">{thousandize(taxBenefit, locale)} kr</text></g></g></>;
      })()}<rect x={padding.left} y={-(dimensions.height - padding.bottom)} width={Math.min(chartWidth, 160)} height={scale(Math.min(value, maximumThreshold))} transform="scale(1,-1)" fill="#000" opacity={value < minimumThreshold ? 0.3 : 1} style={{
        transition: "all 0.5s"
      }} />{taxBenefit > 0 && <rect x={padding.left} y={-(dimensions.height - padding.bottom)} width={Math.min(chartWidth, 160)} height={scale(taxBenefit)} transform="scale(1,-1)" fill="url(#diagonalHatch)" style={{
        transition: "all 0.5s"
      }} />}</svg>;
    $[9] = chartWidth;
    $[10] = dimensions.height;
    $[11] = dimensions.width;
    $[12] = labels?.currentValueLabel;
    $[13] = labels?.maximumThresholdLabel;
    $[14] = labels?.minimumThresholdLabel;
    $[15] = labels?.taxBenefitLabel;
    $[16] = locale;
    $[17] = maximumThreshold;
    $[18] = minimumThreshold;
    $[19] = padding.bottom;
    $[20] = padding.left;
    $[21] = padding.top;
    $[22] = remSize;
    $[23] = scale;
    $[24] = taxBenefit;
    $[25] = value;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t8) {
    t9 = <div ref={containerRef} style={t7}>{t8}</div>;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};